/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { healthRepository } from '@/services/repositories/health.repository.js';

/**
 * Health Records Seeder
 * Creates dummy health data (wounds, diseases, injuries, treatments, vaccinations)
 */

/**
 * Generate a random ISO timestamp within the last X months
 * @param {number} monthsBack
 * @returns {string} ISO timestamp
 */
function randomPastTimestamp(monthsBack = 12) {
  const now = new Date();
  const past = new Date();
  past.setMonth(now.getMonth() - monthsBack);

  const randomTime =
    past.getTime() + Math.random() * (now.getTime() - past.getTime());

  return new Date(randomTime).toISOString();
}

/**
 * Sample health records by type
 */
const healthSamples = {
  wound: [
    { title: 'Leg Cut', description: 'Small laceration on right hind leg, cleaned and monitored' },
    { title: 'Horn Injury', description: 'Minor abrasion on left horn base from fence' },
    { title: 'Foot Wound', description: 'Cut on front left hoof, treated with antiseptic' },
    { title: 'Ear Scratch', description: 'Superficial scratch on ear, healing well' }
  ],
  disease: [
    { title: 'Coccidiosis', description: 'Mild diarrhea, treated with sulfa drugs' },
    { title: 'Respiratory Issue', description: 'Slight cough and nasal discharge, monitored' },
    { title: 'Pink Eye', description: 'Conjunctivitis in left eye, antibiotic treatment' },
    { title: 'Foot Rot', description: 'Early stage foot rot, treated with footbath' }
  ],
  injury: [
    { title: 'Limping', description: 'Favoring right front leg, no visible injury' },
    { title: 'Bruised Hip', description: 'Bumped into fence, mild bruising' },
    { title: 'Swollen Joint', description: 'Left knee slightly swollen, monitored' }
  ],
  treatment: [
    { title: 'Ivermectin', description: 'Dewormer, 1cc per 25kg body weight, subcutaneous' },
    { title: 'Penicillin', description: 'Antibiotic for infection, 5ml IM, 3-day course' },
    { title: 'B-Complex', description: 'Vitamin supplement, 2ml subcutaneous' },
    { title: 'Calcium Gluconate', description: 'For hypocalcemia, 50ml IV slow push' }
  ],
  vaccination: [
    { title: 'CDT Vaccine', description: 'Clostridium perfringens types C & D and tetanus toxoid' },
    { title: 'Rabies Vaccine', description: 'Annual rabies vaccination, 1ml subcutaneous' },
    { title: 'Caseous Lymphadenitis', description: 'CL vaccine, 1ml subcutaneous' }
  ]
};

/**
 * Seed health records for a single goat
 * @param {number} goatId
 * @param {number} count - number of health records to create
 * @returns {Promise<number[]>} array of created record IDs
 */
async function seedHealthForGoat(goatId, count = 8) {
  console.log(`  🐐 Seeding ${count} health records for goat ${goatId}...`);
  const createdIds = [];

  // Mix of different types
  const types = ['wound', 'disease', 'injury', 'treatment', 'vaccination'];
  const entries = [];

  for (let i = 0; i < count; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const samples = healthSamples[type];
    const sample = samples[Math.floor(Math.random() * samples.length)];

    entries.push({
      goatId,
      type,
      title: sample.title,
      description: sample.description,
      measuredAt: randomPastTimestamp(12)
    });
  }

  // Sort by measured_at to simulate chronological order
  entries.sort((a, b) => new Date(a.measuredAt) - new Date(b.measuredAt));

  for (const entry of entries) {
    const id = await healthRepository.create(entry);
    createdIds.push(id);
    console.log(`    ✅ Created ${entry.type}: ${entry.title} (id: ${id})`);
  }

  return createdIds;
}

/**
 * Seed health records for a list of goats
 * @param {number[]} goatIds - array of goat IDs to create health records for
 * @param {number} recordsPerGoat - number of health records per goat
 * @returns {Promise<void>}
 */
export async function seedHealth(goatIds = [], recordsPerGoat = 8) {
  if (!goatIds.length) {
    console.log('⚠️ No goat IDs provided for health seeding. Skipping health records.');
    return;
  }

  console.log(
    `🏥 Seeding health records for ${goatIds.length} goats (${recordsPerGoat} per goat)...`
  );

  try {
    for (const goatId of goatIds) {
      await seedHealthForGoat(goatId, recordsPerGoat);
    }

    console.log('✅ Finished seeding health records.');
  } catch (error) {
    console.error('❌ Failed to seed health records:', error);
    throw error;
  }
}
